import React, { useMemo } from 'react';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { V2CustomerBillBillStatisticsPostResponse } from '@maxtropy/device-customer-apis-v2';
import styles from '@/pages/EnergyBill/index.module.scss';
import { isNil } from 'lodash-es';

interface BillTableProps {
  chartData?: V2CustomerBillBillStatisticsPostResponse;
}

interface TableDataType {
  key: string;
  mediumName: string;
  mediumId?: string | number;
  physicalUnitName?: string;
  [key: string]: string | number | undefined; // 动态列
}

const parseNumber = (value: unknown) => {
  if (isNil(value) || value === '') return null;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const shouldConvertToTenThousand = (values: unknown[]) => {
  const validValues = values.map(parseNumber).filter((value): value is number => !isNil(value));
  return validValues.length > 0 && validValues.every(value => value >= 10000);
};

const BillTable: React.FC<BillTableProps> = ({ chartData }) => {
  // 生成表格数据
  const { columns, dataSource } = useMemo(() => {
    if (!chartData?.chartList?.length) {
      return { columns: [], dataSource: [] };
    }

    // 获取所有能源介质名称（去重，保持顺序，最多12条）
    const mediumNames = new Set<string>();
    chartData.chartList.forEach(item => {
      item.detailList?.forEach(detail => {
        if (detail.mediumName) {
          mediumNames.add(detail.mediumName);
        }
      });
    });
    const mediumList = Array.from(mediumNames).slice(0, 12);

    // 生成列配置 - 使用嵌套结构实现两层表头
    const cols: ColumnsType<TableDataType> = [
      {
        title: '能源介质',
        dataIndex: 'mediumName',
        key: 'mediumName',
        fixed: 'left',
        width: 120,
      },
    ];

    // 添加时间列 - 每个时间点下有成本和用量两个子列
    chartData.chartList.forEach((item, index) => {
      const time = item.time || '';
      // 去掉时间最后一个字：'2026 年' -> '2026', '2026-4 月' -> '2026-4'
      const displayTime = time.slice(0, -1);
      cols.push({
        title: displayTime,
        align: 'left' as const,
        children: [
          {
            title: '成本',
            dataIndex: `cost_${index}`,
            key: `cost_${index}`,
            width: 120,
            render: (value: number | string) => value,
          },
          {
            title: '用量',
            dataIndex: `usage_${index}`,
            key: `usage_${index}`,
            width: 120,
            render: (value: string | number, record: TableDataType) => {
              if (value === '-') return '-';
              const unit = record.physicalUnitName || '';
              return <span style={{ color: '#52E7FF' }}>{`${Number(value).toFixed(2)}${unit ? ` ${unit}` : ''}`}</span>;
            },
          },
        ],
      });
    });

    // 生成数据源
    const data: TableDataType[] = mediumList.map(mediumName => {
      const row: TableDataType = {
        key: mediumName,
        mediumName: mediumName,
      };
      const details = chartData?.chartList?.map(item => item.detailList?.find(d => d.mediumName === mediumName)) ?? [];
      const shouldConvertCost = shouldConvertToTenThousand(details.map(detail => detail?.cost));
      const shouldConvertUsage = shouldConvertToTenThousand(details.map(detail => detail?.usage));
      const usageUnit = details.find(detail => detail?.physicalUnitName)?.physicalUnitName || '';

      chartData?.chartList?.forEach((item, index) => {
        const detail = item.detailList?.find(d => d.mediumName === mediumName);
        const cost = parseNumber(detail?.cost);
        const finalCost = !isNil(cost)
          ? shouldConvertCost
            ? (cost / 10000).toFixed(2) + '万元'
            : cost.toFixed(2) + '元'
          : '-';
        const usage = parseNumber(detail?.usage);
        if (detail?.mediumId) {
          row.mediumId = detail.mediumId;
        }
        row.physicalUnitName = shouldConvertUsage && usageUnit ? `万${usageUnit}` : usageUnit;
        row[`cost_${index}`] = finalCost;
        row[`usage_${index}`] = !isNil(usage) ? usage / (shouldConvertUsage ? 10000 : 1) : '-';
      });

      return row;
    });

    // 添加总成本行（首行）
    const totalRow: TableDataType = {
      key: 'total',
      mediumName: '总成本',
    };
    const shouldConvertTotalCost = shouldConvertToTenThousand(chartData?.chartList?.map(item => item.totalCost) ?? []);
    chartData?.chartList?.forEach((item, index) => {
      const totalCost = parseNumber(item.totalCost);
      const displayTotalCost = !isNil(totalCost) ? (totalCost / (shouldConvertTotalCost ? 10000 : 1)).toFixed(2) : '-';
      const unit = !isNil(totalCost) ? (shouldConvertTotalCost ? '万元' : '元') : '';
      totalRow[`cost_${index}`] = `${displayTotalCost}${unit}`;
      totalRow[`usage_${index}`] = '-';
    });

    // 将总成本行放在最前面
    data.unshift(totalRow);

    return { columns: cols, dataSource: data };
  }, [chartData]);

  if (!chartData?.chartList?.length) {
    return <div style={{ padding: 24, textAlign: 'center', color: '#999' }}>暂无数据</div>;
  }

  return (
    <Table
      columns={columns}
      dataSource={dataSource}
      pagination={false}
      scroll={{ x: 'max-content' }}
      bordered
      style={{ marginTop: 20 }}
      rowClassName={record => (record.key === 'total' ? styles.totalRow : '')}
    />
  );
};

export default BillTable;
